import { type PropsWithChildren, type FunctionComponent, useEffect, useMemo, useRef } from 'react'
import { isEqual } from 'lodash-es'
import { Loader } from '@retailer-platform/shared-components/src/common/loader/Loader'
import { ErrorPage } from '../../../gin-and-tonic/containers/error-page/ErrorPage'
import { hasOnlyRestrictedBanners } from '../../../legacy/components/AccessControl/accessControl.utils'
import { usePartnerId } from '../../routing/params.hooks'
import { useMeasureTiming } from '../../events/hooks'
import { useFetchPartner } from '../partner/queries/partner'
import { isSearchDrivenStorePickerPartner } from '../../searchDrivenStorePicker'
import { useFetchWarehouse } from './queries/warehouse'
import { WarehouseContext, type WarehouseContextValue } from './WarehouseContext'
import { StaleDataNotification } from './StaleDataNotification'

interface Props {
  ids: string
}

const initialState: WarehouseContextValue = {
  loaded: false,
  loading: false,
  error: false,
  // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
  partnerId: undefined,
  // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
  partnerName: undefined,
  // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
  partnerType: undefined,
  mainPartnerId: undefined,
  // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
  warehouse: undefined,
  warehouses: undefined,
  // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
  validId: undefined,
  // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
  locationFeatures: undefined,
  // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
  featureToggles: undefined,
  // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
  featureEntitlements: undefined,
  // @ts-expect-error TS2322 — strictNullChecks. TODO: fix strict null check
  isRestricted: undefined,
}

const WarehouseProvider: FunctionComponent<PropsWithChildren<Props>> = ({ children, ids }) => {
  const triggerMeasureEnd = useMeasureTiming(
    {
      id: 'warehouse_provider.loaded',
      description:
        'Time taken to load application initialization data (retailer locations, retailer locales, etc) from WarehouseProvider mount to full load. Note that there may be additional variance due to retailer loading time (not cached due to roulette flags living there)',
    },
    []
  )

  const warehouseIds = useMemo(() => ids.split('-'), [ids])
  const id = warehouseIds[0]

  const partnerId = Number(usePartnerId())

  const { warehouses, partnerFeatureToggles } = useFetchPartner(partnerId)

  const {
    warehouse,
    partnerName,
    partnerType,
    mainPartnerId,
    locationFeatures,
    featureToggles,
    featureEntitlements,
    loading,
    error,
    errorWhileRevalidating,
    refetch,
  } = useFetchWarehouse(
    { id },
    { skipLocationsQuery: isSearchDrivenStorePickerPartner(partnerFeatureToggles) }
  )

  // Check if ALL warehouses assigned to the user (via partner) are restricted
  // Only block access if the user has no access to non-restricted warehouses
  const isRestricted = hasOnlyRestrictedBanners(warehouses)

  const filteredWarehouses = useMemo(
    // @ts-expect-error TS7006 — strictNullChecks. TODO: fix strict null check
    () => warehouses?.filter(warehouse => warehouseIds.includes(String(warehouse.id))),
    [warehouses, warehouseIds]
  )

  const isInvalidId = id == '0' || id == 'undefined'

  // Derive context value synchronously during render instead of via effects.
  // This eliminates the re-render cascade that was caused by 4 separate
  // useEffect hooks each calling setContextValue.
  // @ts-expect-error TS2345 — strictNullChecks. TODO: fix strict null check
  const contextValue = useMemo<WarehouseContextValue>(() => {
    if (isInvalidId) {
      return {
        loaded: false,
        partnerName: undefined,
        partnerType: undefined,
        mainPartnerId: undefined,
        partnerId,
        loading: true,
        error: false,
        warehouse: undefined,
        warehouses: [],
        validId: false,
        locationFeatures: undefined,
        featureToggles: [],
        featureEntitlements: undefined,
        isRestricted: undefined,
      }
    }
    if (error) {
      return {
        loaded: false,
        loading: false,
        error: true,
        partnerId: undefined,
        partnerName: undefined,
        partnerType: undefined,
        mainPartnerId: undefined,
        warehouse: undefined,
        warehouses: undefined,
        validId: undefined,
        locationFeatures: undefined,
        featureToggles: undefined,
        featureEntitlements: undefined,
        isRestricted: undefined,
      }
    }
    if (warehouse && filteredWarehouses) {
      return {
        loaded: true,
        loading: false,
        error: false,
        partnerName,
        partnerId,
        partnerType,
        mainPartnerId,
        warehouse,
        warehouses: filteredWarehouses,
        validId: true,
        locationFeatures,
        featureToggles,
        featureEntitlements,
        isRestricted,
      }
    }
    if (loading) {
      return {
        loaded: false,
        loading: true,
        error: false,
        partnerId: undefined,
        partnerName: undefined,
        partnerType: undefined,
        mainPartnerId: undefined,
        warehouse: undefined,
        warehouses: undefined,
        validId: undefined,
        locationFeatures: undefined,
        featureToggles: undefined,
        featureEntitlements: undefined,
        isRestricted: undefined,
      }
    }
    return initialState
  }, [
    isInvalidId,
    error,
    warehouse,
    filteredWarehouses,
    loading,
    partnerId,
    partnerName,
    partnerType,
    mainPartnerId,
    locationFeatures,
    featureToggles,
    featureEntitlements,
    isRestricted,
  ])

  // Stabilize the context reference: only produce a new object when the
  // value is deeply different. This prevents unnecessary consumer re-renders
  // when hooks return new object references for identical data.
  const stableContextRef = useRef(contextValue)
  if (!isEqual(stableContextRef.current, contextValue)) {
    stableContextRef.current = contextValue
  }

  useEffect(() => {
    if (!loading && !error && warehouse)
      triggerMeasureEnd({
        retailerId: id,
        cacheEnabled: !!featureToggles
          // @ts-expect-error TS7006 — strictNullChecks. TODO: fix strict null check
          .map?.(v => v.name)
          .includes?.('rt_retailer_stale_while_revalidate_enabled'),
      })
  }, [error, featureToggles, id, loading, triggerMeasureEnd, warehouse])

  if (error) return <ErrorPage action={() => refetch()} />

  // when Id is not valid ( === '0') then it means we haven't selected a retailer yet
  // In that case we want to showChildren, since we need to display the scope selector
  const showChildren = stableContextRef.current.loaded || stableContextRef.current.validId === false

  return (
    <WarehouseContext.Provider value={stableContextRef.current}>
      {errorWhileRevalidating && <StaleDataNotification refetch={refetch} />}

      {showChildren ? children : <Loader />}
    </WarehouseContext.Provider>
  )
}

export default WarehouseProvider
